import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    X, RefreshCw, Loader2, Database, Clock, ChevronDown, ChevronUp,
    User, Briefcase, DollarSign, FileText, AlertCircle, CheckCircle,
    Hash, Calendar, MapPin,
} from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import dataConnectionService from '../../../services/dataConnection.service';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatDate(d) {
    if (!d) return '—';
    return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

function formatValue(v) {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'boolean') return v ? 'Sim' : 'Não';
    if (typeof v === 'number') {
        // monetary heuristic: has "valor", "salario", "renda" in context — handled by caller
        return v.toLocaleString('pt-BR');
    }
    if (typeof v === 'string') {
        // ISO date
        if (/^\d{4}-\d{2}-\d{2}(T|$)/.test(v)) {
            const d = new Date(v);
            return isNaN(d) ? v : d.toLocaleDateString('pt-BR');
        }
        return v;
    }
    return String(v);
}

function formatCurrency(v) {
    if (v === null || v === undefined) return '—';
    const n = parseFloat(v);
    if (isNaN(n)) return String(v);
    return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

const CURRENCY_KEYS = /valor|salario|renda|remuneracao|bruto|liquido|desconto|beneficio|inss|fgts|credito|proposta|honorario/i;
const DATE_KEYS     = /data|nascimento|admissao|demissao|vencimento|inicio|fim|periodo/i;
const ID_KEYS       = /cpf|cnpj|pis|nit|matricula|protocolo|processo|numero|cod\b|codigo/i;
const PHONE_KEYS    = /telefone|celular|fone|ddd/i;
const EMAIL_KEYS    = /email|e-mail/i;
const ADDR_KEYS     = /endereco|logradouro|bairro|cidade|municipio|uf|estado|cep/i;
const EMPLOYER_KEYS = /empresa|empregador|cnpj_empregador|razao|estabelecimento/i;

const SECTION_ICONS = {
    pessoal:     <User className="w-3.5 h-3.5" />,
    endereco:    <MapPin className="w-3.5 h-3.5" />,
    emprego:     <Briefcase className="w-3.5 h-3.5" />,
    financeiro:  <DollarSign className="w-3.5 h-3.5" />,
    documentos:  <FileText className="w-3.5 h-3.5" />,
    outros:      <Hash className="w-3.5 h-3.5" />,
};

function classifyKey(k) {
    if (EMAIL_KEYS.test(k))    return 'pessoal';
    if (PHONE_KEYS.test(k))    return 'pessoal';
    if (ADDR_KEYS.test(k))     return 'endereco';
    if (EMPLOYER_KEYS.test(k)) return 'emprego';
    if (DATE_KEYS.test(k) && /(admissao|demissao|emprego|cargo|funcao)/i.test(k)) return 'emprego';
    if (CURRENCY_KEYS.test(k)) return 'financeiro';
    if (ID_KEYS.test(k))       return 'documentos';
    if (/(nome|sexo|genero|nascimento|idade|raca|estado_civil|naturalidade)/i.test(k)) return 'pessoal';
    if (ADDR_KEYS.test(k))     return 'endereco';
    return 'outros';
}

function labelKey(k) {
    return k
        .replace(/_/g, ' ')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/\b\w/g, c => c.toUpperCase());
}

// ---------------------------------------------------------------------------
// Field row — suporta valor primitivo, objeto ou array (expansível)
// ---------------------------------------------------------------------------
function Field({ label, value, isCurrency, isId }) {
    const [open, setOpen] = useState(false);
    const isNested = value !== null && typeof value === 'object';
    const isArr = Array.isArray(value);

    if (isNested) {
        const entries = isArr
            ? value.map((v, i) => [String(i), v])
            : Object.entries(value);
        const preview = isArr
            ? `${value.length} item${value.length !== 1 ? 's' : ''}`
            : Object.entries(value).map(([k, v]) => `${labelKey(k)}: ${typeof v === 'object' ? '…' : v}`).join(' · ').slice(0, 60);
        return (
            <div className="border-b border-border last:border-0">
                <button
                    type="button"
                    onClick={() => setOpen(v => !v)}
                    className="w-full flex items-start gap-2 py-1.5 text-left hover:bg-muted transition-colors"
                >
                    <span className="text-xs text-muted-foreground w-36 shrink-0 pt-0.5 truncate">{label}</span>
                    <span className="text-xs flex-1 text-muted-foreground italic truncate">{preview || '(vazio)'}</span>
                    {open ? <ChevronUp className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" /> : <ChevronDown className="w-3 h-3 text-muted-foreground shrink-0 mt-0.5" />}
                </button>
                {open && (
                    <div className="ml-36 pl-2 border-l-2 border-border mb-1 space-y-0">
                        {entries.map(([k, v]) => (
                            <Field
                                key={k}
                                label={isArr ? `[${k}]` : labelKey(k)}
                                value={v}
                                isCurrency={CURRENCY_KEYS.test(k)}
                                isId={ID_KEYS.test(k)}
                            />
                        ))}
                    </div>
                )}
            </div>
        );
    }

    const display = isCurrency ? formatCurrency(value) : formatValue(value);
    return (
        <div className="flex items-start gap-2 py-1.5 border-b border-border last:border-0">
            <span className="text-xs text-muted-foreground w-36 shrink-0 pt-0.5 truncate">{label}</span>
            <span className={`text-xs flex-1 break-all ${isId ? 'font-mono text-foreground' : 'text-foreground'} ${isCurrency ? 'font-semibold text-emerald-700 font-mono' : ''}`}>
                {display}
            </span>
        </div>
    );
}

// ---------------------------------------------------------------------------
// Section card
// ---------------------------------------------------------------------------
function Section({ title, icon, fields, data }) {
    const [open, setOpen] = useState(true);
    return (
        <div className="rounded-xl border border-border overflow-hidden">
            <button
                onClick={() => setOpen(v => !v)}
                className="w-full flex items-center justify-between px-3 py-2 bg-muted hover:bg-muted/80 transition-colors"
            >
                <div className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                    <span className="text-muted-foreground">{icon}</span>
                    {title}
                </div>
                {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground" /> : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground" />}
            </button>
            {open && (
                <div className="px-3 py-1">
                    {fields.map(k => (
                        <Field
                            key={k}
                            label={labelKey(k)}
                            value={data[k]}
                            isCurrency={CURRENCY_KEYS.test(k)}
                            isId={ID_KEYS.test(k)}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Smart result viewer — handles flat obj, nested obj, array
// ---------------------------------------------------------------------------
function QueryResultViewer({ data }) {
    if (!data) return <p className="text-xs text-muted-foreground italic py-2">Sem dados retornados</p>;

    const obj = typeof data === 'string' ? (() => { try { return JSON.parse(data); } catch { return null; } })() : data;

    if (!obj || typeof obj !== 'object') {
        return <pre className="text-xs bg-muted border border-border rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-all">{String(data)}</pre>;
    }

    // Flatten one level of nesting for well-known wrappers
    let flat = obj;
    const wrapperKeys = ['dados', 'data', 'resultado', 'result', 'pessoa', 'trabalhador'];
    for (const k of wrapperKeys) {
        if (obj[k] && typeof obj[k] === 'object' && !Array.isArray(obj[k])) {
            flat = { ...obj, ...obj[k] };
            delete flat[k];
            break;
        }
    }

    // Separate scalar fields from nested objects/arrays
    const scalars = Object.entries(flat).filter(([, v]) => !v || typeof v !== 'object');
    const nested  = Object.entries(flat).filter(([, v]) => v && typeof v === 'object');

    // Group scalar fields into sections
    const sections = {};
    scalars.forEach(([k]) => {
        const sec = classifyKey(k);
        if (!sections[sec]) sections[sec] = [];
        sections[sec].push(k);
    });

    const sectionOrder = ['pessoal', 'endereco', 'emprego', 'financeiro', 'documentos', 'outros'];
    const sectionLabels = {
        pessoal: 'Dados Pessoais',
        endereco: 'Endereço',
        emprego: 'Vínculo Empregatício',
        financeiro: 'Dados Financeiros',
        documentos: 'Documentos / IDs',
        outros: 'Outros',
    };

    return (
        <div className="space-y-2 mt-1">
            {sectionOrder
                .filter(s => sections[s]?.length)
                .map(s => (
                    <Section
                        key={s}
                        title={sectionLabels[s]}
                        icon={SECTION_ICONS[s]}
                        fields={sections[s]}
                        data={flat}
                    />
                ))
            }
            {nested.map(([k, v]) => (
                <div key={k} className="rounded-xl border border-border overflow-hidden">
                    <div className="flex items-center gap-1.5 px-3 py-2 bg-muted text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                        {SECTION_ICONS.outros}
                        {labelKey(k)}
                    </div>
                    <div className="px-3 py-2">
                        {Array.isArray(v) ? (
                            v.length === 0
                                ? <p className="text-xs text-muted-foreground italic">Lista vazia</p>
                                : v.map((item, i) => (
                                    <div key={i} className="mb-2 last:mb-0">
                                        {typeof item === 'object'
                                            ? Object.entries(item).map(([ik, iv]) => (
                                                <Field key={ik} label={labelKey(ik)} value={iv}
                                                    isCurrency={CURRENCY_KEYS.test(ik)} isId={ID_KEYS.test(ik)} />
                                            ))
                                            : <span className="text-xs font-mono text-foreground">{String(item)}</span>
                                        }
                                    </div>
                                ))
                        ) : (
                            Object.entries(v).map(([ik, iv]) => (
                                <Field key={ik} label={labelKey(ik)} value={iv}
                                    isCurrency={CURRENCY_KEYS.test(ik)} isId={ID_KEYS.test(ik)} />
                            ))
                        )}
                    </div>
                </div>
            ))}
        </div>
    );
}

// ---------------------------------------------------------------------------
// History entry
// ---------------------------------------------------------------------------
function QueryEntry({ q, defaultOpen, connMap = {} }) {
    const [open, setOpen] = useState(defaultOpen);
    const ok = !q.error_message;
    return (
        <div className="rounded-xl border border-border overflow-hidden">
            <button
                onClick={() => setOpen(v => !v)}
                className="w-full flex items-start gap-2 px-3 py-2.5 hover:bg-muted transition-colors text-left"
            >
                <span className="mt-0.5 shrink-0">
                    {ok
                        ? <CheckCircle className="w-3.5 h-3.5 text-emerald-500" />
                        : <AlertCircle className="w-3.5 h-3.5 text-rose-400" />}
                </span>
                <div className="flex-1 min-w-0">
                    <p className="text-xs font-semibold text-foreground">
                        {connMap[q.data_connection_id]?.name || q.query_type}
                    </p>
                    <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                        <span className="font-mono">{q.query_type}</span>
                        <span>·</span>
                        <Calendar className="w-3 h-3" />{formatDate(q.created_at)}
                    </p>
                </div>
                {open ? <ChevronUp className="w-3.5 h-3.5 text-muted-foreground mt-1 shrink-0" />
                      : <ChevronDown className="w-3.5 h-3.5 text-muted-foreground mt-1 shrink-0" />}
            </button>
            {open && (
                <div className="border-t border-border px-3 pb-3 pt-2">
                    {q.error_message
                        ? <p className="text-xs text-rose-600 bg-rose-50 rounded-lg px-3 py-2">{q.error_message}</p>
                        : <QueryResultViewer data={q.result_data ?? q.data} />
                    }
                </div>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------
export function ExternalQueryPanel({ cedenteId, processoCedenteId, cedenteNome, onClose }) {
    const [connections, setConnections] = useState([]);
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [running, setRunning] = useState(null);
    const connMap = useMemo(() => Object.fromEntries(connections.map(c => [c.id, c])), [connections]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [conns, hist] = await Promise.all([
                dataConnectionService.list().catch(() => []),
                dataConnectionService.listExternalQueries(cedenteId).catch(() => []),
            ]);
            setConnections(conns.filter(c => c.is_active));
            setHistory(hist);
        } finally {
            setLoading(false);
        }
    }, [cedenteId]);

    useEffect(() => { load(); }, [load]);

    const handleRun = async (queryType, connId) => {
        setRunning(connId);
        try {
            const res = await dataConnectionService.runExternalQuery(cedenteId, {
                query_type: queryType,
                processo_cedente_id: processoCedenteId,
            });
            toast.success('Consulta realizada');
            setHistory(prev => [res.query, ...prev]);
        } catch (e) {
            toast.error('Erro: ' + (e?.response?.data?.detail || e.message));
        } finally {
            setRunning(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-2 sm:px-4">
            <div className="bg-card rounded-2xl shadow-xl w-full max-w-2xl max-h-[92vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-5 py-4 border-b border-border shrink-0">
                    <div className="flex items-center gap-2">
                        <Database className="w-4 h-4 text-blue-500" />
                        <div>
                            <p className="text-sm font-semibold text-foreground">Consulta de dados externos</p>
                            {cedenteNome && <p className="text-xs text-muted-foreground">{cedenteNome}</p>}
                        </div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1">
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 px-5 py-4 space-y-5">
                    {loading ? (
                        <div className="flex items-center justify-center py-12 text-muted-foreground gap-2">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            <span className="text-sm">Carregando...</span>
                        </div>
                    ) : (
                        <>
                            {/* Connections */}
                            {connections.length > 0 && (
                                <section>
                                    <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-2">Fontes disponíveis</p>
                                    <div className="flex flex-col gap-2">
                                        {connections.map(c => (
                                            <div key={c.id} className="flex items-center justify-between rounded-xl border border-border bg-muted/60 px-4 py-3">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-lg bg-blue-100 flex items-center justify-center">
                                                        <Database className="w-3.5 h-3.5 text-blue-600" />
                                                    </div>
                                                    <div>
                                                        <p className="text-sm font-medium text-foreground">{c.name}</p>
                                                        <p className="text-xs text-muted-foreground font-mono">{c.connection_type}</p>
                                                    </div>
                                                </div>
                                                <Button
                                                    size="sm"
                                                    disabled={!!running}
                                                    onClick={() => handleRun(c.connection_type, c.id)}
                                                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-4 h-8 gap-1.5"
                                                >
                                                    {running === c.id
                                                        ? <><Loader2 className="w-3 h-3 animate-spin" /> Consultando…</>
                                                        : <><RefreshCw className="w-3 h-3" /> Consultar</>}
                                                </Button>
                                            </div>
                                        ))}
                                    </div>
                                </section>
                            )}

                            {/* History */}
                            <section>
                                <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-widest mb-2 flex items-center gap-1.5">
                                    <Clock className="w-3 h-3" /> Histórico de consultas
                                </p>
                                {history.length === 0 ? (
                                    <div className="text-center py-10 text-muted-foreground border border-dashed border-border rounded-xl">
                                        <Database className="w-7 h-7 mx-auto mb-2 opacity-30" />
                                        <p className="text-sm">Nenhuma consulta realizada ainda</p>
                                    </div>
                                ) : (
                                    <div className="flex flex-col gap-2">
                                        {history.slice(0, 15).map((q, i) => (
                                            <QueryEntry key={q.id || i} q={q} defaultOpen={i === 0} connMap={connMap} />
                                        ))}
                                    </div>
                                )}
                            </section>
                        </>
                    )}
                </div>

                <div className="px-5 py-3 border-t border-border flex justify-end shrink-0">
                    <Button variant="outline" size="sm" onClick={onClose}>Fechar</Button>
                </div>
            </div>
        </div>
    );
}
